import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

declare module "@mui/material/styles" {
  interface Theme {
    modalBreakPoints: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
    };
    border: {
      default: string;
      gray100: string;
    };
    bg: {
      appBg: string;
      default: string;
      paper: string;
      shadow: string;
      [100]: string;
      [200]: string;
      [300]: string;
      [400]: string;
      [500]: string;

      red: string;
      backdrop: string;
      darkBlue: string;
      blue: string;
    };
    skeleton: {
      bg: string;
      item: string;
    };
  }
  // allow configuration using `createTheme()`
  interface ThemeOptions {
    modalBreakPoints?: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
    };
    border?: {
      default: string;
      gray100: string;
    };
    bg?: {
      appBg: string;
      default: string;
      paper: string;
      shadow: string;
      [100]: string;
      [200]: string;
      [300]: string;
      [400]: string;
      [500]: string;
      red: string;
      backdrop: string;
      darkBlue: string;
      blue: string;
    };
    skeleton: {
      bg: string;
      item: string;
    };
  }
}

// A custom theme for this app
export const lightTheme = createTheme({
  modalBreakPoints: {
    xs: "500px",
    sm: "600px",
    md: "960px",
    lg: "1360px",
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1360,
      xl: 1536,
    },
  },
  border: { default: "#F2F2F2", gray100: "#F3F4F6" },
  bg: {
    appBg: "#F5F5F5",
    default: "",
    paper: "white",
    shadow: "gray",
    100: "#F5F5F5",
    200: "#F7F9FC",
    300: "#CDEDFE",
    400: "#FAFAFA",
    500: "#FAFAFA",

    red: "#FEE2E2",
    backdrop: "rgba(0, 0, 0, 0.40)",
    darkBlue: "rgba(206, 235, 248, 0.25)",
    blue: "#EAF7FE",
  },
  skeleton: {
    bg: "#EEEEEE",
    item: "grey.900",
  },
  palette: {
    mode: "light",
    primary: {
      contrastText: "#EFF2FE",
      dark: "#0572d1", // hover
      light: "#E7EDF2",
      main: "#2B7FFF",
      100: "#E6F6FF",
      200: "#83D1FD",
    },
    info: {
      main: "#F2F6FC",
      light: "#F4F4F6",
      dark: "#07c8fc",
    },
    secondary: {
      main: "#FAFAFA",
      light: "#CEEBF8",
      dark: "#353535", // hover
      100: "#F2F2F2",
      200: "#E5E5E5",
      300: "#8F92A14D",
      400: "#686868",
      500: "#555555",
    },
    background: {
      default: "#F5F5F5",
      paper: "#fff",
    },
    common: {
      white: "#F1F7FB",
      black: "#FAFAFA",
    },
    text: {
      primary: "#000",
      secondary: "#18171D",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // Apply textTransform: none to all buttons
        },
      },
    },
    // MuiDataGrid: {
    //   styleOverrides: {
    //     row: {
    //       "&:hover": {
    //         backgroundColor: "transparent !important",
    //       },
    //     },
    //   },
    // },
  },
  typography: {
    body1: {
      fontSize: "16px",
      fontWeight: 500,
      letterSpacing: "0.00938em",
      color: "#2F2F2F", // Light gray body 1 color
    },
  },
});

// Define the custom dark theme
export const darkTheme = createTheme({
  modalBreakPoints: {
    xs: "500px",
    sm: "600px",
    md: "960px",
    lg: "1360px",
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1360,
      xl: 1536,
    },
  },
  border: { default: "#363636", gray100: "#1E2939" },
  bg: {
    appBg: "#060B15",
    default: "",
    // paper: "#222029",
    paper: "#131620",

    shadow: "black",
    100: "#313645",
    200: "#313645",
    300: "rgba(7, 164, 252, 0.16)",
    400: "#0D0D0D",
    red: "rgb(153 76 76)",
    backdrop: "rgb(0 0 0 / 71%)",
    darkBlue: "rgba(10, 52, 71, 0.25)",
    blue: "#01314C",
    500: "#121212",
  },
  skeleton: {
    bg: "#18171D",
    item: "grey.200",
  },
  palette: {
    mode: "dark",
    primary: {
      contrastText: "#151515",
      main: "#2B7FFF",
      dark: "#0572d1", // hover
      light: "#008CDB",
      100: "#404659",
    },
    secondary: {
      main: "#23293d",
      light: "#4D5EA4",
      dark: "#0E0E0E",
      100: "#1A1A1A",
      200: "#494747",
      300: "#fff",
      400: "#686868",
      500: "#686868",
    },
    info: {
      dark: "#07c8fc",
      main: "#0C1118",
      light: "#1F2021",
    },
    text: {
      primary: "#ffffff",
      secondary: "#9f9f9f",
    },
    common: {
      white: "#282A31",
      black: "white",
    },
    background: {
      default: "#18171D",
      paper: "#222029",
    },
  },
  typography: {
    fontFamily: "Roboto, sans-serif",
    h1: {
      fontSize: "4rem",
      fontWeight: 700,
      letterSpacing: "-0.01562em",
      color: "#ffffff", // White heading 1 color
    },
    h2: {
      fontSize: "3rem",
      fontWeight: 700,
      letterSpacing: "-0.00833em",
      color: "#c2c2c2", // Light gray heading 2 color
    },
    h3: {
      fontSize: "2.25rem",
      fontWeight: 700,
      letterSpacing: "0",
      color: "#e0e0e0", // Slightly lighter gray heading 3 color
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      letterSpacing: "0.00735em",
      color: "#ffffff", // White heading 4 color
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      letterSpacing: "0.00714em",
      color: "#ffffff", // White heading 5 color
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      letterSpacing: "0.0075em",
      color: "#ffffff", // White heading 6 color
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 400,
      letterSpacing: "0.00938em",
      color: "#b3b3b3", // Light gray subtitle 1 color
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      letterSpacing: "0.00714em",
      color: "#a0a0a0", // Gray subtitle 2 color
    },
    body1: {
      fontSize: "16px",
      fontWeight: 500,
      letterSpacing: "0.00938em",
      color: "#ffffff", // Light gray body 1 color
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      letterSpacing: "0.01071em",
      color: "#ffffff", // White heading 1 color
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      letterSpacing: "0.03333em",
      color: "#a0a0a0", // Gray caption color
    },
    overline: {
      fontSize: "0.625rem",
      fontWeight: 400,
      letterSpacing: "0.16667em",
      color: "#ffffff", // White overline color
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // Apply textTransform: none to all buttons
        },
      },
    },
  },
});
